import { useCallback } from 'react';

/**
 * usePositionHandlers Hook
 * 
 * Handles node position changes (single and batch) with undo support.
 */
export const usePositionHandlers = ({
  localSprint,
  setLocalSprint,
  onRenameSprint,
  recordAction,
  startBatch,
  endBatch,
  isMultiCampaignMode = false,
}) => {
  // Handle renaming the sprint
  // Updates local state AND calls global callback to sync with sidebar
  const handleRenameSprint = useCallback((newName) => {
    setLocalSprint(prev => ({ ...prev, name: newName }));
    // Also update the global sprints array so sidebar reflects the change immediately
    if (onRenameSprint && localSprint?.id) {
      onRenameSprint(localSprint.id, newName);
    }
  }, [setLocalSprint, onRenameSprint, localSprint?.id]);

  // Handle position change (when nodes are dragged)
  // oldPosition is required from useFlowchartDrag - we always have it now
  const handlePositionChange = useCallback((taskId, position, oldPosition = null) => {
    // Use provided oldPosition, or try to get from task.x/y as fallback
    const task = localSprint?.tasks?.find(t => t.id === taskId);
    
    // previousPosition: prefer explicit oldPosition, fallback to task.x/y
    // If neither available (shouldn't happen), use {x:0, y:0} to ensure undo is still recorded
    let previousPosition = oldPosition;
    if (!previousPosition && task) {
      // Only use task.x/y if they are defined numbers
      if (typeof task.x === 'number' && typeof task.y === 'number') {
        previousPosition = { x: task.x, y: task.y };
      }
    }
    // Last resort fallback - use the new position minus a tiny offset to ensure undo records
    // This shouldn't happen with the fixed useFlowchartDrag
    if (!previousPosition) {
      previousPosition = { x: position.x, y: position.y };
    }

    // In multi-campaign mode, subtract the campaign offset to get absolute position
    // The view position includes horizontal offset for side-by-side display
    let absolutePosition = position;
    if (isMultiCampaignMode && task?._campaignOffset) {
      absolutePosition = {
        x: position.x - task._campaignOffset,
        y: position.y, // Y is not affected by offset
      };
    }

    setLocalSprint(prev => {
      if (!prev) return prev;
      return {
        ...prev,
        tasks: prev.tasks.map(t => 
          t.id === taskId 
            ? { ...t, x: absolutePosition.x, y: absolutePosition.y }
            : t
        ),
      };
    });

    // Record for undo (only if we have a previous position and it actually changed)
    if (recordAction && previousPosition && 
        (previousPosition.x !== absolutePosition.x || previousPosition.y !== absolutePosition.y)) {
      const taskName = task?.name || 'Node';
      recordAction({
        type: 'MOVE_NODE',
        description: `Move: "${taskName}"`,
        undo: () => {
          setLocalSprint(prev => {
            if (!prev) return prev;
            return {
              ...prev,
              tasks: prev.tasks.map(t => 
                t.id === taskId 
                  ? { ...t, x: previousPosition.x, y: previousPosition.y }
                  : t
              ),
            };
          });
        },
        redo: () => {
          setLocalSprint(prev => {
            if (!prev) return prev;
            return {
              ...prev,
              tasks: prev.tasks.map(t => 
                t.id === taskId 
                  ? { ...t, x: absolutePosition.x, y: absolutePosition.y }
                  : t
              ),
            };
          });
        },
      });
    }
  }, [setLocalSprint, localSprint?.tasks, recordAction, isMultiCampaignMode]);

  // Handle batched position changes (for multi-node drag or tidy)
  // positionChanges: Array of { taskId, position, oldPosition }
  // options: { batchType, batchDescription, skipRecordAction } - optional custom batch labeling (e.g., for Tidy)
  //          skipRecordAction: if true, don't record undo action (used when tidy is part of another action like insert)
  const handlePositionChangeBatch = useCallback((positionChanges, options = {}) => {
    if (!positionChanges || positionChanges.length === 0) return;
    
    // Capture all task data for undo, converting to absolute positions if in multi-campaign mode
    const changesWithData = positionChanges.map(change => {
      const task = localSprint?.tasks?.find(t => t.id === change.taskId);
      
      // In multi-campaign mode, subtract the campaign offset to get absolute position
      let absolutePosition = change.position;
      if (isMultiCampaignMode && task?._campaignOffset) {
        absolutePosition = {
          x: change.position.x - task._campaignOffset,
          y: change.position.y,
        };
      }
      
      // Determine old position - prefer explicit, then task.x/y (if defined), then fallback
      let oldPosition = change.oldPosition;
      if (!oldPosition && task && typeof task.x === 'number' && typeof task.y === 'number') {
        oldPosition = { x: task.x, y: task.y };
      }
      if (!oldPosition) {
        oldPosition = { x: 0, y: 0 };
      }
      
      return {
        ...change,
        position: absolutePosition, // Use absolute position for saving
        taskName: task?.name || 'Node',
        oldPosition,
      };
    }).filter(change => 
      // Only include if position actually changed (both values must be numbers)
      (typeof change.oldPosition.x === 'number' && typeof change.oldPosition.y === 'number') &&
      (change.oldPosition.x !== change.position.x || change.oldPosition.y !== change.position.y)
    );
    
    if (changesWithData.length === 0) return;
    
    // Apply all position changes at once
    setLocalSprint(prev => {
      if (!prev) return prev;
      const posMap = new Map(changesWithData.map(c => [c.taskId, c.position]));
      return {
        ...prev,
        tasks: prev.tasks.map(t => {
          const newPos = posMap.get(t.id);
          return newPos ? { ...t, x: newPos.x, y: newPos.y } : t;
        }),
      };
    });
    
    // Record as a single grouped action (unless skipRecordAction is true)
    if (recordAction && startBatch && endBatch && !options.skipRecordAction) {
      const count = changesWithData.length;
      // Use custom batch type/description if provided (e.g., for Tidy operation)
      const batchType = options.batchType || 'MOVE_NODES';
      const batchDescription = options.batchDescription || `Move ${count} item${count > 1 ? 's' : ''}`;
      startBatch(batchType, batchDescription);
      
      changesWithData.forEach(change => {
        recordAction({
          type: 'MOVE_NODE',
          description: `Move: "${change.taskName}"`,
          undo: () => {
            setLocalSprint(prev => {
              if (!prev) return prev;
              return {
                ...prev,
                tasks: prev.tasks.map(t => 
                  t.id === change.taskId 
                    ? { ...t, x: change.oldPosition.x, y: change.oldPosition.y }
                    : t
                ),
              };
            });
          },
          redo: () => {
            setLocalSprint(prev => {
              if (!prev) return prev;
              return {
                ...prev,
                tasks: prev.tasks.map(t => 
                  t.id === change.taskId 
                    ? { ...t, x: change.position.x, y: change.position.y }
                    : t
                ),
              };
            });
          },
        });
      });
      
      endBatch();
    }
  }, [setLocalSprint, localSprint?.tasks, recordAction, startBatch, endBatch, isMultiCampaignMode]);

  return {
    handleRenameSprint,
    handlePositionChange,
    handlePositionChangeBatch,
  };
};

export default usePositionHandlers;
